import type { ApiError, Identity } from "@agriflow/shared";

const API: string = typeof import.meta.env.VITE_API_BASE_URL === "string" ? import.meta.env.VITE_API_BASE_URL : "";
export class ApiClientError extends Error {
  constructor(public readonly code: string, message: string, public readonly details: unknown) { super(message); }
}
function tenantHeaders(): HeadersInit {
  const organizationId = localStorage.getItem("agriflow.organization");
  return organizationId ? { "X-Organization-Id": organizationId } : {};
}
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API}/api/v1${path}`, {
    ...init, credentials: "include",
    headers: { "Content-Type": "application/json", ...tenantHeaders(), ...init.headers }
  });
  if (!response.ok) {
    const body = await response.json() as ApiError;
    throw new ApiClientError(body.error.code, body.error.message, body.error.details);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
export const api = {
  me: () => request<{ data: Identity }>("/auth/me"),
  profiles: () => request<{ data: Array<{ id: string; displayName: string; email: string }> }>("/auth/development/profiles"),
  login: (userId: string) => request("/auth/development/session", { method: "POST", body: JSON.stringify({ userId }) }),
  loginWithMammouth: (token: string) => request("/auth/mammouth/session", { method: "POST", body: JSON.stringify({ token }) }),
  logout: () => request("/auth/session", { method: "DELETE" }),
  dashboard: () => request<{ data: { farms: number; fields: number; totalAreaHa: number; plannedInterventions: number; stockMovements: number } }>("/dashboard"),
  farms: () => request<{ data: { items: Farm[]; total: number } }>("/farms"),
  createFarm: (body: Record<string, unknown>) => request<{ data: Farm }>("/farms", { method: "POST", body: JSON.stringify(body) }),
  archiveFarm: (id: string) => request<{ data: { id: string; archived: true } }>(`/farms/${id}`, { method: "DELETE" }),
  fields: () => request<{ data: { items: Field[]; total: number } }>("/fields"),
  createField: (body: Record<string, unknown>) => request<{ data: Field }>("/fields", { method: "POST", body: JSON.stringify(body) }),
  archiveField: (id: string) => request<void>(`/fields/${id}`, { method: "DELETE" }),
  list: <T = Record<string, unknown>>(path: string) => request<{ data: T[] }>(path),
  create: <T = Record<string, unknown>>(path: string, body: Record<string, unknown>) => request<{ data: T }>(path, { method: "POST", body: JSON.stringify(body) }),
  updateInterventionStatus: (id: string, status: string) => request(`/interventions/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  reportSummary: () => request<{ data: { farms: number; interventions: number; equipment: number } }>("/reports/summary"),
  uploadDocument: async (file: File) => {
    const contentBase64 = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(typeof reader.result === "string" ? (reader.result.split(",")[1] ?? "") : ""); reader.onerror = () => reject(reader.error ?? new Error("Lecture du fichier impossible.")); reader.readAsDataURL(file); });
    return request("/documents", { method: "POST", body: JSON.stringify({ name: file.name, mimeType: file.type || "application/octet-stream", contentBase64 }) });
  },
  downloadDocument: async (id: string, filename: string) => {
    const response = await fetch(`${API}/api/v1/documents/${id}/download`, { credentials: "include", headers: tenantHeaders() });
    if (!response.ok) throw new ApiClientError("DOWNLOAD_FAILED", "Le document n’a pas pu être téléchargé.", {});
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  },
  createOrganization: (name: string) => request("/organizations", { method: "POST", body: JSON.stringify({ name, countryCode: "FR" }) })
};
export interface Farm { id: string; name: string; registrationNumber: string | null; address: string | null; totalAreaHa: string | null; status: string; }
export interface Field { id: string; farmId: string; name: string; reference: string | null; areaHa: string; cropName: string | null; status: string; }
